import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Plus, 
  FileSignature,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Send,
  TrendingUp,
  ArrowRight,
  Eye,
  BarChart3,
  PieChart,
  Activity,
  Zap,
  Shield,
  Layers,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Button, Badge } from '../../../components/ui';
import { PageHeader, InfoTooltip, ContextualHelp } from '../../../components/help';
import { contractsHelp } from '../../../config/helpContent';

interface DashboardStats {
  totalContracts: number;
  totalTemplates: number;
  pendingSignatures: number;
  completedThisMonth: number;
  averageSignTime: number;
  signatureRate: number;
  byStatus: {
    DRAFT: number;
    PENDING_SIGNATURES: number;
    PARTIALLY_SIGNED: number;
    COMPLETED: number;
    EXPIRED: number;
    CANCELLED: number;
  };
  byCategory: {
    category: string;
    count: number;
  }[];
  recentActivity: {
    id: string;
    type: 'created' | 'sent' | 'signed' | 'completed';
    contractId: string;
    contractName: string;
    timestamp: string;
    user?: string;
  }[];
  monthlyTrend: {
    month: string;
    created: number;
    completed: number;
  }[];
}

interface RecentContract {
  id: string;
  template: { name: string; category: string };
  status: string;
  createdAt: string;
  completedAt?: string;
  signatures: { signerName: string; status: string }[];
}

const STATUS_CONFIG = {
  DRAFT: { label: 'Rascunho', bgColor: 'bg-surface-700', textColor: 'text-surface-300', icon: FileText },
  PENDING_SIGNATURES: { label: 'Aguardando', bgColor: 'bg-blue-500/20', textColor: 'text-blue-400', icon: Send },
  PARTIALLY_SIGNED: { label: 'Parcial', bgColor: 'bg-yellow-500/20', textColor: 'text-yellow-400', icon: AlertTriangle },
  COMPLETED: { label: 'Concluído', bgColor: 'bg-green-500/20', textColor: 'text-green-400', icon: CheckCircle },
  EXPIRED: { label: 'Expirado', bgColor: 'bg-red-500/20', textColor: 'text-red-400', icon: Clock },
  CANCELLED: { label: 'Cancelado', bgColor: 'bg-surface-700', textColor: 'text-surface-500', icon: XCircle },
};

export function ContractsDashboard() {
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['contracts-dashboard-stats', dateRange],
    queryFn: async () => {
      const response = await api.get<DashboardStats>(`/contracts/dashboard/stats?range=${dateRange}`);
      return response.data;
    },
  });

  const { data: recentContracts, isLoading: contractsLoading } = useQuery({
    queryKey: ['contracts-recent'],
    queryFn: async () => {
      const response = await api.get<RecentContract[]>('/contracts?take=5&orderBy=createdAt&order=desc');
      return response.data;
    },
  });

  const isLoading = statsLoading || contractsLoading;

  // Calculate percentages for the donut chart
  const totalByStatus = stats ? Object.values(stats.byStatus).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="min-h-screen p-6">
      {/* Header with Help */}
      <PageHeader
        title="Contratos Digitais"
        description="Gerencie contratos, templates e assinaturas eletrônicas com validade jurídica."
        icon={<FileSignature className="w-6 h-6 text-primary-400" />}
        module="Contratos Digitais"
        helpContent={contractsHelp.dashboard.helpContent}
      />

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 mb-6">
        <Button variant="outline" onClick={() => refetchStats()} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </Button>
        <Link to="/admin/contracts/new">
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            Novo Contrato
          </Button>
        </Link>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Link to="/admin/contracts/new" className="group">
          <Card className="hover:border-primary-500/50 transition-all duration-300 h-full">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl text-white shadow-lg shadow-primary-500/20">
                  <FileSignature className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-surface-100 group-hover:text-primary-400 transition-colors">Criar Contrato</p>
                  <p className="text-sm text-surface-500">A partir de template</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/contracts/templates/new" className="group">
          <Card className="hover:border-purple-500/50 transition-all duration-300 h-full">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl text-white shadow-lg shadow-purple-500/20">
                  <Layers className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-surface-100 group-hover:text-purple-400 transition-colors">Novo Template</p>
                  <p className="text-sm text-surface-500">Criar modelo</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/contracts/list?status=PENDING_SIGNATURES" className="group">
          <Card className="hover:border-yellow-500/50 transition-all duration-300 h-full">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl text-white shadow-lg shadow-yellow-500/20">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-surface-100 group-hover:text-yellow-400 transition-colors">Pendentes</p>
                  <p className="text-sm text-surface-500">{stats?.pendingSignatures || 0} aguardando</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/contracts/templates" className="group">
          <Card className="hover:border-green-500/50 transition-all duration-300 h-full">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl text-white shadow-lg shadow-green-500/20">
                  <FileText className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-surface-100 group-hover:text-green-400 transition-colors">Templates</p>
                  <p className="text-sm text-surface-500">{stats?.totalTemplates || 0} modelos</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="bg-surface-800/50 border-surface-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-sm font-medium text-surface-400 uppercase tracking-wide">Total de Contratos</p>
                  <InfoTooltip content="Número total de contratos criados no tenant, independente do status." />
                </div>
                <p className="text-3xl font-bold text-surface-100 mt-2">{stats?.totalContracts || 0}</p>
                <p className="text-sm text-green-400 mt-1 flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" />
                  +12% este mês
                </p>
              </div>
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <FileSignature className="w-8 h-8 text-primary-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-surface-800/50 border-surface-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-sm font-medium text-surface-400 uppercase tracking-wide">Concluídos (Mês)</p>
                  <InfoTooltip content="Contratos que tiveram todas as assinaturas coletadas neste mês." />
                </div>
                <p className="text-3xl font-bold text-surface-100 mt-2">{stats?.completedThisMonth || 0}</p>
                <p className="text-sm text-surface-500 mt-1">
                  Taxa: {stats?.signatureRate?.toFixed(1) || 0}%
                </p>
              </div>
              <div className="p-4 bg-green-500/20 rounded-2xl">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-surface-800/50 border-surface-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-sm font-medium text-surface-400 uppercase tracking-wide">Aguardando Assinatura</p>
                  <InfoTooltip content="Contratos enviados que ainda não foram assinados por todos os signatários." />
                </div>
                <p className="text-3xl font-bold text-surface-100 mt-2">{stats?.pendingSignatures || 0}</p>
                <p className="text-sm text-yellow-400 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Requer atenção
                </p>
              </div>
              <div className="p-4 bg-yellow-500/20 rounded-2xl">
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-surface-800/50 border-surface-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-sm font-medium text-surface-400 uppercase tracking-wide">Tempo Médio</p>
                  <InfoTooltip content="Tempo médio entre o envio do contrato e a coleta de todas as assinaturas." />
                </div>
                <p className="text-3xl font-bold text-surface-100 mt-2">{stats?.averageSignTime || 0}h</p>
                <p className="text-sm text-surface-500 mt-1">
                  Para conclusão
                </p>
              </div>
              <div className="p-4 bg-purple-500/20 rounded-2xl">
                <Zap className="w-8 h-8 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Status Distribution */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-surface-100">
              <PieChart className="w-5 h-5 text-primary-400" />
              Distribuição por Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.byStatus && Object.entries(stats.byStatus).map(([status, count]) => {
                const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];
                const percentage = totalByStatus > 0 ? (count / totalByStatus * 100).toFixed(1) : 0;
                const Icon = config?.icon || FileText;
                
                return (
                  <div key={status} className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${config?.bgColor || 'bg-surface-700'}`}>
                      <Icon className={`w-4 h-4 ${config?.textColor || 'text-surface-400'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-surface-200">{config?.label || status}</span>
                        <span className="text-sm text-surface-400">{count}</span>
                      </div>
                      <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            status === 'COMPLETED' ? 'bg-green-500' :
                            status === 'PENDING_SIGNATURES' ? 'bg-blue-500' :
                            status === 'PARTIALLY_SIGNED' ? 'bg-yellow-500' :
                            status === 'EXPIRED' ? 'bg-red-500' :
                            'bg-surface-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-surface-500 w-12 text-right">{percentage}%</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recent Contracts */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2 text-surface-100">
              <Activity className="w-5 h-5 text-primary-400" />
              Contratos Recentes
            </CardTitle>
            <Link to="/admin/contracts/list">
              <Button variant="ghost" size="sm" className="gap-1 text-primary-400 hover:text-primary-300">
                Ver todos
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {contractsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse flex items-center gap-4 p-4 bg-surface-800 rounded-xl">
                    <div className="w-10 h-10 bg-surface-700 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-surface-700 rounded w-1/3" />
                      <div className="h-3 bg-surface-700 rounded w-1/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentContracts && recentContracts.length > 0 ? (
              <div className="space-y-3">
                {recentContracts.map((contract) => {
                  const statusConfig = STATUS_CONFIG[contract.status as keyof typeof STATUS_CONFIG];
                  const StatusIcon = statusConfig?.icon || FileText;
                  
                  return (
                    <Link 
                      key={contract.id} 
                      to={`/admin/contracts/${contract.id}`}
                      className="flex items-center gap-4 p-4 bg-surface-800/50 hover:bg-surface-800 rounded-xl transition-colors group border border-surface-700 hover:border-surface-600"
                    >
                      <div className={`p-2.5 rounded-xl ${statusConfig?.bgColor || 'bg-surface-700'}`}>
                        <StatusIcon className={`w-5 h-5 ${statusConfig?.textColor || 'text-surface-400'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-surface-100 truncate group-hover:text-primary-400 transition-colors">
                          {contract.template?.name || 'Contrato'}
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-surface-500">
                            {format(new Date(contract.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </span>
                          <span className="text-xs text-surface-600">•</span>
                          <span className="text-xs text-surface-500">
                            {contract.signatures?.length || 0} assinante(s)
                          </span>
                        </div>
                      </div>
                      <Badge variant={contract.status === 'COMPLETED' ? 'success' : contract.status === 'PENDING_SIGNATURES' ? 'info' : 'default'}>
                        {statusConfig?.label || contract.status}
                      </Badge>
                      <ArrowRight className="w-4 h-4 text-surface-500 group-hover:text-primary-400 transition-colors" />
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhum contrato encontrado</p>
                <Link to="/admin/contracts/new">
                  <Button variant="outline" size="sm" className="mt-3">
                    Criar primeiro contrato
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Features Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-primary-500/10 to-primary-600/5 border-primary-500/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary-500 rounded-xl text-white">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-surface-100 mb-1">Assinatura Segura</h3>
                <p className="text-sm text-surface-400">
                  Assinatura eletrônica com verificação biométrica facial e validação jurídica completa.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-500 rounded-xl text-white">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-surface-100 mb-1">Automação Inteligente</h3>
                <p className="text-sm text-surface-400">
                  Variáveis dinâmicas, preenchimento automático e integração com tokenização de ativos.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-green-500 rounded-xl text-white">
                <BarChart3 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-surface-100 mb-1">Rastreabilidade Total</h3>
                <p className="text-sm text-surface-400">
                  Histórico completo, verificação via QR Code e registro em blockchain opcional.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contextual Help FAQs */}
      <ContextualHelp faqs={contractsHelp.faqs} />
    </div>
  );
}
